import * as React from 'react';
import { PortableText } from '@portabletext/react';
import copy from 'copy-to-clipboard';
import NextImage from 'next/image';
import toast from 'react-hot-toast';
import { urlForImage } from 'services/server/sanity/image';
import ExternalLink from 'svg/ExternalLink';
import XIcon from 'svg/XIcon';
import FaceBookIcon from 'svg/FaceBookIcon';
import LinkedInIcon from 'svg/LinkedInIcon';
import InstagramIcon from 'svg/InstagramIcon';
import TikTokIcon from 'svg/TikTokIcon';
import YoutubeIcon from 'svg/YoutubeIcon';

interface Props {
  author: {
    name: string;
    jobTitle?: string;
    image?: any;
    bio?: any[]; // PortableText blocks
    socialLinks?: {
      twitter?: string;
      facebook?: string;
      linkedin?: string;
      instagram?: string;
      tiktok?: string;
      youtube?: string;
    };
  };
  articleUrl?: string;
}

export default function AuthorCard({ author, articleUrl }: Props) {
  const { name, jobTitle, image, bio, socialLinks } = author;

  const imageUrl = image ? urlForImage(image) : '';

  // Check if there are any social links
  const hasSocialLinks =
    socialLinks &&
    (socialLinks.twitter || socialLinks.facebook || socialLinks.linkedin || socialLinks.instagram || socialLinks.tiktok || socialLinks.youtube);

  return (
    <div className="rounded-lg border border-color-border-input-lightmode p-6 dark:border-color-border-input-darkmode md:p-8">
      <div className="flex flex-col gap-6 md:flex-row md:gap-8">
        {/* Author Avatar */}
        {imageUrl && (
          <div className="shrink-0">
            <div className="relative h-20 w-20 overflow-hidden rounded-full md:h-24 md:w-24">
              <NextImage src={imageUrl} alt={name} fill className="object-cover" />
            </div>
          </div>
        )}

        {/* Author Info */}
        <div className="flex-1">
          {/* Name and Title */}
          <div className="mb-3">
            <h3 className="typography-product-heading mb-1 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {name}
            </h3>
            {jobTitle && (
              <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                {jobTitle}
              </p>
            )}
          </div>

          {/* Bio */}
          {bio && bio.length > 0 && (
            <div className="typography-product-body prose mb-4 text-color-text-lightmode-primary dark:prose-invert dark:text-color-text-darkmode-primary">
              <PortableText value={bio} />
            </div>
          )}

        </div>
      </div>

      {/* Footer row: Social Links + Share */}
      {(hasSocialLinks || articleUrl) && (
        <>
          <div className="mt-4 border-t border-color-border-input-lightmode dark:border-color-border-input-darkmode" />
          <div className="mt-4 flex items-center justify-between">
            {/* Author social profiles */}
            {hasSocialLinks ? (
              <div className="flex items-center gap-3">
                {socialLinks?.linkedin && (
                  <a
                    href={socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full p-2 transition-colors hover:bg-color-bg-lightmode-secondary dark:hover:bg-color-bg-darkmode-secondary"
                    aria-label="Follow on LinkedIn"
                  >
                    <LinkedInIcon className="h-5 w-5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                  </a>
                )}
                {socialLinks?.instagram && (
                  <a
                    href={socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full p-2 transition-colors hover:bg-color-bg-lightmode-secondary dark:hover:bg-color-bg-darkmode-secondary"
                    aria-label="Follow on Instagram"
                  >
                    <InstagramIcon className="h-5 w-5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                  </a>
                )}
                {socialLinks?.tiktok && (
                  <a
                    href={socialLinks.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full p-2 transition-colors hover:bg-color-bg-lightmode-secondary dark:hover:bg-color-bg-darkmode-secondary"
                    aria-label="Follow on TikTok"
                  >
                    <TikTokIcon className="h-5 w-5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                  </a>
                )}
                {socialLinks?.youtube && (
                  <a
                    href={socialLinks.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full p-2 transition-colors hover:bg-color-bg-lightmode-secondary dark:hover:bg-color-bg-darkmode-secondary"
                    aria-label="Follow on YouTube"
                  >
                    <YoutubeIcon className="h-5 w-5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                  </a>
                )}
                {socialLinks?.twitter && (
                  <a
                    href={socialLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full p-2 transition-colors hover:bg-color-bg-lightmode-secondary dark:hover:bg-color-bg-darkmode-secondary"
                    aria-label="Follow on X/Twitter"
                  >
                    <XIcon className="h-5 w-5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                  </a>
                )}
                {socialLinks?.facebook && (
                  <a
                    href={socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full p-2 transition-colors hover:bg-color-bg-lightmode-secondary dark:hover:bg-color-bg-darkmode-secondary"
                    aria-label="Follow on Facebook"
                  >
                    <FaceBookIcon className="h-5 w-5 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                  </a>
                )}
              </div>
            ) : (
              <div />
            )}

            {/* Share article */}
            {articleUrl && (
              <button
                onClick={() => {
                  copy(articleUrl);
                  toast.success('Link copied to clipboard');
                }}
                className="flex items-center gap-2 rounded-full border border-color-border-input-lightmode px-4 py-2 transition-colors hover:bg-color-bg-lightmode-secondary dark:border-color-border-input-darkmode dark:hover:bg-color-bg-darkmode-secondary"
              >
                <ExternalLink className="h-4 w-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                <span className="typography-product-body text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  Share
                </span>
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
